import { PenLine, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";

/** Sube, previsualiza y elimina la firma del profesional (PNG/JPG/WEBP, máx. 2 MB).
 *  La firma se embebe automáticamente en los PDFs de receta. */
export function SignatureUploader({ user }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [hasSignature, setHasSignature] = useState(!!user?.signature_path);

  // Carga la firma actual del backend como blob para mostrarla
  useEffect(() => {
    let url = null;
    let cancelled = false;
    if (!hasSignature) {
      setPreviewUrl(null);
      return undefined;
    }
    api
      .get("/auth/profile/signature", { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        url = URL.createObjectURL(r.data);
        setPreviewUrl(url);
      })
      .catch(() => {
        if (!cancelled) setPreviewUrl(null);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [hasSignature, user?.signature_updated_at]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post("/auth/profile/signature", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Firma actualizada");
      setHasSignature(true);
      // Forzar recarga del blob
      setPreviewUrl(null);
      setTimeout(() => setHasSignature(true), 0);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setBusy(true);
    try {
      await api.delete("/auth/profile/signature");
      toast.success("Firma eliminada");
      setHasSignature(false);
      setPreviewUrl(null);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border rounded-2xl p-4 bg-secondary/30" data-testid="signature-uploader">
      <div className="flex items-center gap-2 mb-1">
        <PenLine className="h-4 w-4 text-primary" strokeWidth={1.5} />
        <Label className="text-sm font-semibold">Firma para recetas</Label>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Sube tu firma en imagen (PNG, JPG o WEBP) con fondo blanco o transparente. Se mostrará al
        pie de cada receta y nota de evolución que envíes. Tamaño máximo: 2 MB.
      </p>
      {previewUrl ? (
        <div className="bg-white border rounded-xl p-3 mb-3 flex items-center justify-center">
          <img src={previewUrl} alt="Firma" className="max-h-24 object-contain" data-testid="signature-preview" />
        </div>
      ) : hasSignature ? (
        <p className="text-xs text-muted-foreground mb-3">Cargando vista previa...</p>
      ) : (
        <p className="text-xs text-muted-foreground italic mb-3" data-testid="signature-empty-hint">
          Aún no has subido una firma. Tus recetas saldrán solo con tu nombre.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={onPick}
          data-testid="signature-file-input"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-full"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          data-testid="signature-upload-btn"
        >
          <Upload className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
          {hasSignature ? "Reemplazar firma" : "Subir firma"}
        </Button>
        {hasSignature && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full border-red-300 text-red-700 hover:bg-red-50"
            onClick={onDelete}
            disabled={busy}
            data-testid="signature-delete-btn"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Quitar
          </Button>
        )}
      </div>
    </div>
  );
}
